import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { ConfigConstants } from 'utils/config';
import {
  playNotificationSound,
  unlockNotificationSound
} from 'utils/notificationSound';
import { toastConfig } from 'utils/toastConfig';
import { Token, TNotification } from 'utils/types';
import { AWAITING_COUNTS_KEY } from './useAwaitingProcessingCounts';

const NOTIFICATION_LIST_KEY = ['notifications', 'list'] as const;
const NOTIFICATION_UNREAD_COUNT_KEY = ['notifications', 'unreadCount'] as const;

export type TNotificationPage = {
  notifications: TNotification[];
  currentPage: number;
  pageCount: number;
  pageSize: number;
  rowCount: number;
};

/**
 * The API stores CreatedOn in UTC but serializes it without a timezone suffix
 * (DateTimeKind is lost on the SQL round-trip), so mark it as UTC explicitly;
 * otherwise dayjs().fromNow() reads it as local time and skews by the offset.
 */
const toUtcIsoString = (value: string): string =>
  value && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}Z` : value;

const normalizeNotification = (
  raw: Record<string, unknown>
): TNotification | null => {
  const notificationId = Number(raw.notificationId ?? raw.NotificationId);
  if (!notificationId) return null;

  const urlValue = raw.url ?? raw.Url;
  return {
    notificationId,
    type: (raw.type ?? raw.Type ?? '') as string | number,
    title: String(raw.title ?? raw.Title ?? ''),
    body: String(raw.body ?? raw.Body ?? ''),
    url: urlValue == null ? null : String(urlValue),
    isRead: Boolean(raw.isRead ?? raw.IsRead),
    createdOn: toUtcIsoString(String(raw.createdOn ?? raw.CreatedOn ?? ''))
  };
};

/** Maps API paged notification payload (PagedResult shape) into a stable frontend model. */
export const mapNotificationPageResponse = (
  responseData: unknown
): TNotificationPage => {
  const payload =
    (responseData as { data?: unknown })?.data != null &&
    !Array.isArray((responseData as { data?: unknown }).data) &&
    typeof (responseData as { data?: unknown }).data === 'object'
      ? (responseData as { data: Record<string, unknown> }).data
      : (responseData as Record<string, unknown>);

  const rows = (payload.results ??
    payload.data ??
    payload.Data ??
    []) as Record<string, unknown>[];

  const notifications = rows
    .map(normalizeNotification)
    .filter((notification): notification is TNotification => notification != null);

  return {
    notifications,
    currentPage: Number(payload.currentPage ?? payload.CurrentPage ?? 1),
    pageCount: Number(payload.pageCount ?? payload.PageCount ?? 1),
    pageSize: Number(payload.pageSize ?? payload.PageSize ?? 20),
    rowCount: Number(payload.rowCount ?? payload.RowCount ?? notifications.length)
  };
};

const fetchNotificationPage = async (
  page: number,
  pageSize: number
): Promise<TNotificationPage> => {
  const { data } = await axiosInstance.get('/api/v1/notifications', {
    params: { page, pageSize }
  });
  return mapNotificationPageResponse(data);
};

const fetchUnreadCount = async (): Promise<number> => {
  const { data } = await axiosInstance.get('/api/v1/notifications/unreadCount');
  if (typeof data === 'number') return data;
  const wrapped = (data as { data?: unknown })?.data ?? data;
  return typeof wrapped === 'number' ? wrapped : 0;
};

const readAccessToken = (): string => {
  try {
    return (JSON.parse(localStorage.getItem('token') || '{}') as Token)
      .accessToken;
  } catch {
    return '';
  }
};

/** Live badge/list updates. Falls back to the unread-count poll if the hub drops. */
type NotificationCreatedPayload = {
  title?: string;
  body?: string;
  url?: string | null;
};

export const useNotificationRealtime = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!readAccessToken()) return;

    const baseUrl = (ConfigConstants.baseUrl || '').replace(/\/?$/, '/');
    const connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}hubs/notifications`, {
        accessTokenFactory: readAccessToken,
        withCredentials: false,
        transport:
          HttpTransportType.WebSockets |
          HttpTransportType.ServerSentEvents |
          HttpTransportType.LongPolling
      })
      .withAutomaticReconnect()
      .build();

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'page'] });
      queryClient.invalidateQueries({ queryKey: AWAITING_COUNTS_KEY });
    };

    const onCreated = (payload?: NotificationCreatedPayload) => {
      invalidate();

      const raw = (payload ?? {}) as Record<string, unknown>;
      const title = String(raw.title ?? raw.Title ?? 'New notification');
      const body = String(raw.body ?? raw.Body ?? '');
      const url = (raw.url ?? raw.Url) as string | null | undefined;

      playNotificationSound();
      toast.info(
        <div>
          <div className="fw-bold">{title}</div>
          {body ? (
            <div className="fs-9 text-body-secondary mt-1">{body}</div>
          ) : null}
        </div>,
        {
          ...toastConfig,
          toastId: `${title}|${body}`,
          autoClose: 6000,
          onClick: () => {
            if (url) navigate(url);
          }
        }
      );
    };

    connection.on('notificationCreated', onCreated);
    connection.start().catch((error: unknown) => {
      console.error('Notification hub failed to start', error);
    });

    const unlock = () => unlockNotificationSound();
    window.addEventListener('pointerdown', unlock, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlock);
      connection.off('notificationCreated', onCreated);
      connection.stop();
    };
  }, [navigate, queryClient]);
};

export const useUnreadNotificationCount = () =>
  useQuery({
    queryKey: NOTIFICATION_UNREAD_COUNT_KEY,
    queryFn: fetchUnreadCount,
    refetchInterval: 60000,
    refetchOnWindowFocus: true
  });

export const useNotificationList = (enabled: boolean) =>
  useQuery({
    queryKey: NOTIFICATION_LIST_KEY,
    queryFn: () => fetchNotificationPage(1, 20),
    enabled
  });

export const useNotificationPage = (page: number) =>
  useQuery({
    queryKey: ['notifications', 'page', page],
    queryFn: () => fetchNotificationPage(page, 20)
  });

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: number) => {
      await axiosInstance.put(`/api/v1/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'page'] });
    }
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axiosInstance.put('/api/v1/notifications/readAll');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_LIST_KEY });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'page'] });
    }
  });
};
